import React from 'react';
import { useTranslation } from 'react-i18next';
import FeaturedDishCard from './FeaturedDishCard';
import { ArrowUpDown, ChevronDown, UtensilsCrossed } from 'lucide-react';

const DishesSection = ({
  dishes,
  dishesLoading,
  currentDishes,
  totalPages,
  currentPage,
  onPageChange,
  onDishCardClick,
  onDishPlusClick,
  sortBy,
  showSortDropdown,
  onSortChange,
  onToggleSortDropdown
}) => {
  const { t } = useTranslation();
  const sortOptions = [
    { value: 'popularity', label: t('home.sortOptions.popularity') },
    { value: 'price-low', label: t('home.sortOptions.priceLow') },
    { value: 'price-high', label: t('home.sortOptions.priceHigh') },
    { value: 'rating', label: t('home.sortOptions.rating') }
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap', marginBottom: '22px' }}>
        <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(22px, 2.6vw, 34px)', letterSpacing: '-0.02em', color: '#054625' }}>
          {t('home.dishes.title')}
        </h3>
        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
          {t('home.dishes.count', { count: dishes ? dishes.length : 0 })}
        </span>
        <div style={{ position: 'relative', marginLeft: 'auto' }}>
          <button
            onClick={onToggleSortDropdown}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: '#FCFBF5',
              border: 'none',
              borderRadius: '999px',
              padding: '10px 18px',
              fontFamily: 'Archivo, sans-serif',
              fontSize: '14px',
              color: '#051004',
              cursor: 'pointer',
            }}
          >
            <ArrowUpDown size={15} color="#054625" />
            <span>{sortOptions.find(opt => opt.value === sortBy)?.label}</span>
            <ChevronDown size={15} />
          </button>

          {showSortDropdown && (
            <div style={{ position: 'absolute', right: 0, marginTop: '8px', width: '200px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', boxShadow: '0 10px 22px rgba(5,16,4,0.1)', zIndex: 10, overflow: 'hidden' }}>
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => onSortChange(option.value)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '11px 18px',
                    fontFamily: 'Archivo, sans-serif',
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer',
                    background: sortBy === option.value ? 'rgba(114,183,68,0.14)' : 'transparent',
                    color: sortBy === option.value ? '#054625' : 'rgba(5,16,4,0.78)',
                    fontWeight: sortBy === option.value ? 600 : 400,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Grille des plats */}
      {dishesLoading ? (
        <div className='grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6'>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="w-full aspect-square rounded-2xl bg-gray-200" />
              <div className="pt-3 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-4/5" />
                <div className="h-3.5 bg-gray-200 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : !currentDishes || currentDishes.length === 0 ? (
        <div className="text-center py-14">
          <UtensilsCrossed className="h-10 w-10 text-namke-darkGreen/25 mx-auto mb-3" />
          <p className="text-namke-black/50 text-sm">{t('home.dishes.noResults')}</p>
        </div>
      ) : (
        <div className='grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6'>
          {currentDishes.map((dish, index) => (
            <FeaturedDishCard
              key={dish?.id ?? `dish-${index}`}
              dish={dish}
              onCardClick={onDishCardClick}
              onPlusClick={onDishPlusClick}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!dishesLoading && totalPages > 1 && (
        <div className="flex justify-center items-center mt-8 mb-8 px-2 max-w-full">
          <div className="flex items-center gap-1 sm:gap-2 bg-namke-white rounded-full border border-namke-darkGreen/15 p-2 max-w-full overflow-x-auto">
            {/* Info de pagination — masquée sur mobile pour laisser la place aux boutons */}
            <div className="hidden sm:block text-sm text-namke-black/55 mr-2 shrink-0">
              {t('home.dishes.pageOf', { current: currentPage, total: totalPages })}
            </div>
            {/* Bouton Précédent */}
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`shrink-0 px-2 sm:px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center ${
                currentPage === 1
                  ? 'text-namke-black/30 cursor-not-allowed bg-namke-cream/60'
                  : 'text-namke-black/75 hover:bg-primary-50'
              }`}
            >
              <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">{t('home.previous')}</span>
            </button>

            {/* Numéros de pages */}
            <div className="flex items-center gap-1 shrink-0">
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNumber = index + 1;
                const isCurrentPage = pageNumber === currentPage;
                const isNearCurrent = Math.abs(pageNumber - currentPage) <= 2;

                if (isNearCurrent || pageNumber === 1 || pageNumber === totalPages) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => onPageChange(pageNumber)}
                      className={`shrink-0 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                        isCurrentPage
                          ? 'text-white'
                          : 'text-namke-black/75 hover:bg-primary-50'
                      }`}
                      style={isCurrentPage ? { backgroundColor: 'rgb(5, 70, 37)' } : undefined}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (pageNumber === 2 && currentPage > 4) {
                  return <span key={pageNumber} className="shrink-0 px-2 text-namke-black/35">...</span>;
                } else if (pageNumber === totalPages - 1 && currentPage < totalPages - 3) {
                  return <span key={pageNumber} className="shrink-0 px-2 text-namke-black/35">...</span>;
                }
                return null;
              })}
            </div>

            {/* Bouton Suivant */}
            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`shrink-0 px-2 sm:px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center ${
                currentPage === totalPages
                  ? 'text-namke-black/30 cursor-not-allowed bg-namke-cream/60'
                  : 'text-namke-black/75 hover:bg-primary-50'
              }`}
            >
              <span className="hidden sm:inline">{t('home.next')}</span>
              <svg className="w-4 h-4 sm:ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DishesSection;


